import React, { useState, useEffect } from 'react';
import './AttachmentSelector.css';

/**
 * AttachmentSelector - Modal for selecting data sources to include in chat context
 *
 * Data sources:
 * - uploadFile: Upload local PDF, JSON, or text file
 * - orderEntry: AI-extracted fields from document analysis
 * - datatrace: DataTrace public property data
 * - taxStatus: Tax status report from DataTrace
 * - titleSearch: Title search results (TIQSA)
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Close modal callback
 * @param {Array} attachments - Currently attached files/data [{type, name, content}]
 * @param {function} onApply - Callback when Apply is clicked with attachments array
 * @param {Object} availableSources - Which sources have data for this order
 * @param {string} orderId - Optional order ID (if null, only show upload option)
 */
function AttachmentSelector({
  isOpen,
  onClose,
  attachments = [],
  onApply,
  availableSources = {},
  orderId = null
}) {
  const [localAttachments, setLocalAttachments] = useState(attachments);
  const [showReportSelector, setShowReportSelector] = useState(null); // 'datatrace', 'taxStatus', or 'titleSearch'
  const [savedReports, setSavedReports] = useState([]);

  // Order number search state
  const [orderNumberInput, setOrderNumberInput] = useState('');
  const [orderSearchLoading, setOrderSearchLoading] = useState(false);
  const [orderSearchError, setOrderSearchError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [localAvailableSources, setLocalAvailableSources] = useState(availableSources);
  const [localOrderId, setLocalOrderId] = useState(orderId);

  // Update local attachments when props change
  useEffect(() => {
    setLocalAttachments(attachments);
  }, [attachments]);

  // Load saved reports when needed
  useEffect(() => {
    if (showReportSelector) {
      loadSavedReports();
    }
  }, [showReportSelector]);

  // Update local state when props change
  useEffect(() => {
    setLocalAvailableSources(availableSources);
    setLocalOrderId(orderId);
  }, [availableSources, orderId]);

  const loadSavedReports = async () => {
    try {
      const response = await fetch('/api/datatrace/saved-reports');
      const data = await response.json();
      setSavedReports(data.reports || []);
    } catch (error) {
      console.error('Error loading saved reports:', error);
      setSavedReports([]);
    }
  };

  const handleOrderSearch = async () => {
    if (!orderNumberInput.trim()) {
      setOrderSearchError('Please enter an order number or ID');
      return;
    }

    setOrderSearchLoading(true);
    setOrderSearchError(null);
    setSelectedOrder(null);

    try {
      const identifier = encodeURIComponent(orderNumberInput.trim());
      const response = await fetch(`/api/orders/search/${identifier}`);

      if (!response.ok) {
        if (response.status === 404) {
          setOrderSearchError('Order not found. Please check the order number.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          setOrderSearchError(errorData.error || 'Failed to search for order');
        }
        return;
      }

      const orderData = await response.json();
      setSelectedOrder(orderData);
      setLocalOrderId(orderData.internalId);

      // Determine which sources have data for this order
      const available = {
        orderEntry: !!(orderData.documentAnalysis?.aiExtractedFields || orderData.extractedData),
        datatrace: !!orderData.datatraceData,
        taxStatus: !!orderData.taxSearchData,
        titleSearch: !!orderData.titleSearchData
      };
      setLocalAvailableSources(available);

    } catch (error) {
      console.error('Order search error:', error);
      setOrderSearchError('Network error. Please try again.');
    } finally {
      setOrderSearchLoading(false);
    }
  };

  // Data source definitions
  const dataSources = [
    {
      id: 'uploadFile',
      name: 'Upload Local File',
      description: 'Upload a PDF, JSON, or text file',
      icon: '📎',
      alwaysAvailable: true,
      isUpload: true
    },
    {
      id: 'orderEntry',
      name: 'Order Entry Data',
      description: 'Borrower, property, and loan information',
      icon: '📄',
      requiresOrder: true
    },
    {
      id: 'datatrace',
      name: 'DataTrace Public Data',
      description: 'Select from saved property reports',
      icon: '🏘️',
      isDataTraceReport: true,
      productCode: 'property_data'
    },
    {
      id: 'taxStatus',
      name: 'Tax Status Report',
      description: 'Select from saved tax reports',
      icon: '💰',
      isDataTraceReport: true,
      productCode: 'tax'
    },
    {
      id: 'titleSearch',
      name: 'Title Search Results (TIQSA)',
      description: 'Select from saved title search reports',
      icon: '📋',
      isDataTraceReport: true,
      productCode: 'tiqsa'
    }
  ];

  const handleSourceClick = async (source) => {
    if (source.isUpload) {
      // Trigger file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.json,.txt';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          await handleFileUpload(file);
        }
      };
      input.click();
    } else if (source.isDataTraceReport) {
      // Show report selector sub-modal
      setShowReportSelector(source.id);
    } else if (source.requiresOrder && localAvailableSources[source.id]) {
      // Fetch order entry data and toggle attachment
      await handleOrderEntrySelect(source);
    }
  };

  const handleOrderEntrySelect = async (source) => {
    try {
      // Fetch the complete order data
      const response = await fetch(`/api/orders/${localOrderId}`);
      if (!response.ok) {
        alert('Failed to load order data. Please try again.');
        return;
      }

      const orderData = await response.json();

      // Extract the AI-extracted fields or extracted data
      const extractedFields = orderData.documentAnalysis?.aiExtractedFields || orderData.extractedData || {};

      // Create attachment with the actual order data content
      toggleAttachment({
        type: source.id,
        name: `${source.name} - ${orderData.orderNumber || localOrderId}`,
        content: JSON.stringify(extractedFields, null, 2),
        sourceId: source.id
      });
    } catch (error) {
      console.error('Error fetching order data:', error);
      alert('Failed to load order data. Please try again.');
    }
  };

  const handleFileUpload = async (file) => {
    try {
      let content = '';

      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        content = await file.text();
      } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        content = await file.text();
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        // For PDFs, we'll store the file and let backend handle extraction
        content = await fileToBase64(file);
      }

      const attachment = {
        type: 'uploadFile',
        name: file.name,
        content: content,
        fileType: file.type
      };

      setLocalAttachments(prev => [...prev, attachment]);
    } catch (error) {
      console.error('Error reading file:', error);
      alert('Error reading file. Please try again.');
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleReportSelect = async (report, sourceId) => {
    try {
      // Load the report JSON content
      const response = await fetch(report.url);
      const content = await response.json();

      const attachment = {
        type: sourceId,
        name: report.filename,
        content: JSON.stringify(content),
        sourceId: sourceId
      };

      setLocalAttachments(prev => [...prev, attachment]);
      setShowReportSelector(null); // Close report selector
    } catch (error) {
      console.error('Error loading report:', error);
      alert('Error loading report. Please try again.');
    }
  };

  const toggleAttachment = (newAttachment) => {
    setLocalAttachments(prev => {
      const exists = prev.find(a => a.sourceId === newAttachment.sourceId);
      if (exists) {
        return prev.filter(a => a.sourceId !== newAttachment.sourceId);
      } else {
        return [...prev, newAttachment];
      }
    });
  };

  const removeAttachment = (index) => {
    setLocalAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleApply = () => {
    onApply(localAttachments);
    onClose();
  };

  const handleCancel = () => {
    setLocalAttachments(attachments); // Reset to original
    setShowReportSelector(null);
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  // Filter reports by product code for the current selector
  const getFilteredReports = () => {
    if (!showReportSelector) return [];

    const source = dataSources.find(s => s.id === showReportSelector);
    if (!source || !source.productCode) return [];

    return savedReports.filter(report => {
      // Match by product code in transaction data
      return report.productCode === source.productCode;
    });
  };

  return (
    <div className="attachment-modal-overlay" onClick={handleCancel}>
      <div className="attachment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="attachment-modal-header">
          <h3>📎 {showReportSelector ? 'Select Report' : 'Add Attachments'}</h3>
          <button className="attachment-modal-close" onClick={handleCancel}>×</button>
        </div>

        <div className="attachment-modal-body">
          {showReportSelector ? (
            // Report selector sub-view
            <>
              <div className="attachment-back-button" onClick={() => setShowReportSelector(null)}>
                ← Back to sources
              </div>
              <p className="attachment-modal-description">
                Select a {dataSources.find(s => s.id === showReportSelector)?.name} report:
              </p>
              <div className="attachment-reports-list">
                {getFilteredReports().length === 0 ? (
                  <div className="attachment-no-reports">
                    No saved reports found for this type.
                  </div>
                ) : (
                  getFilteredReports().map((report, index) => (
                    <div
                      key={index}
                      className="attachment-report-item"
                      onClick={() => handleReportSelect(report, showReportSelector)}
                    >
                      <div className="attachment-report-icon">📄</div>
                      <div className="attachment-report-info">
                        <div className="attachment-report-name">{report.filename}</div>
                        <div className="attachment-report-meta">
                          {new Date(report.modified).toLocaleDateString()} • {(report.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            // Main source selector view
            <>
              {/* Order search section - only show if no orderId provided */}
              {!orderId && (
                <div className="attachment-order-search">
                  <div className="attachment-order-search-header">Search for Order</div>
                  <div className="attachment-order-search-input-wrapper">
                    <input
                      type="text"
                      className="attachment-order-search-input"
                      placeholder="Enter order number (e.g., S400193) or internal ID"
                      value={orderNumberInput}
                      onChange={(e) => setOrderNumberInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleOrderSearch()}
                      disabled={orderSearchLoading}
                    />
                    <button
                      className="attachment-order-search-button"
                      onClick={handleOrderSearch}
                      disabled={orderSearchLoading || !orderNumberInput.trim()}
                    >
                      {orderSearchLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  {orderSearchError && (
                    <div className="attachment-order-search-error">{orderSearchError}</div>
                  )}
                  {selectedOrder && (
                    <div className="attachment-order-search-success">
                      ✓ Found order: {selectedOrder.orderNumber || selectedOrder.internalId}
                    </div>
                  )}
                </div>
              )}

              {localOrderId ? (
                <p className="attachment-modal-description">
                  Add attachments for Order: <code>{localOrderId}</code>
                </p>
              ) : (
                <p className="attachment-modal-description">
                  Add attachments to provide context for your chat:
                </p>
              )}

              {/* Current attachments */}
              {localAttachments.length > 0 && (
                <div className="attachment-current-list">
                  <div className="attachment-current-header">Current Attachments:</div>
                  {localAttachments.map((att, index) => (
                    <div key={index} className="attachment-bubble">
                      <span className="attachment-bubble-icon">
                        {dataSources.find(s => s.id === att.type)?.icon || '📎'}
                      </span>
                      <span className="attachment-bubble-name">{att.name}</span>
                      <button
                        className="attachment-bubble-remove"
                        onClick={() => removeAttachment(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="attachment-sources-list">
                {dataSources.map(source => {
                  // Determine if source is available
                  let isAvailable = source.alwaysAvailable;
                  if (source.requiresOrder) {
                    isAvailable = localOrderId && localAvailableSources[source.id];
                  }
                  if (source.isDataTraceReport) {
                    isAvailable = true; // Always show, will check reports in sub-view
                  }

                  // Check if this source type is already attached
                  const isAttached = localAttachments.some(a => a.sourceId === source.id);

                  return (
                    <div
                      key={source.id}
                      className={`attachment-source-item ${!isAvailable ? 'unavailable' : ''} ${isAttached ? 'selected' : ''}`}
                      onClick={() => isAvailable && handleSourceClick(source)}
                    >
                      <div className="attachment-source-icon">{source.icon}</div>
                      <div className="attachment-source-info">
                        <div className="attachment-source-name">
                          {source.name}
                          {!isAvailable && <span className="attachment-unavailable-badge">Not Available</span>}
                          {source.isDataTraceReport && <span className="attachment-selector-arrow">→</span>}
                        </div>
                        <div className="attachment-source-description">{source.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="attachment-modal-footer">
            <div className="attachment-selection-count">
              {localAttachments.length} attachment{localAttachments.length !== 1 ? 's' : ''}
            </div>
            <div className="attachment-modal-actions">
              <button className="attachment-btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button className="attachment-btn-apply" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AttachmentSelector;
